import {
  Alert,
  Checkbox,
  CheckboxGroup,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import useSWR from "swr";

import { CanvasCore } from "../../lib/canvas";
import { api } from "../../lib/utils";
import { Button } from "../core/Button";
import { type IRule } from "../Info/InfoText";
import { SmallCanvas } from "./SmallCanvas";

interface IPixel {
  id: number;
  userId: string;
  x: number;
  y: number;
  color: string;
  createdAt: Date;
}

export const ReportPixelModal = ({
  open,
  onOpen,
  pixel,
}: {
  open: boolean;
  onOpen: (v: boolean) => any;
  pixel?: IPixel;
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [surrounding, setSurrounding] = useState<string[][]>();
  const meta = useSWR<{ rules: IRule[] }>("/api/info");

  const [rules, setRules] = useState<string[]>([]);
  const [comment, setComment] = useState<string>("");

  useEffect(() => {
    setError(undefined);
  }, [open]);

  useEffect(() => {
    if (pixel && CanvasCore.get()) {
      const surrounding = CanvasCore.get().getSurroundingPixels(
        pixel.x,
        pixel.y,
        3,
      );

      setSurrounding(surrounding);
    } else {
      setSurrounding(undefined);
    }
  }, [open, pixel]);

  const doSubmit = useCallback(() => {
    setLoading(true);
    if (!pixel) {
      alert("pixel is not defined");
      return;
    }

    api("/api/reports", "POST", {
      pixelId: pixel.id,
      rules,
      comment,
    })
      .then(({ status, data }) => {
        if (status === 201 && data.success) {
          toast.success("Report has been created, thank you");
          onOpen(false);
        } else {
          if (data.success) {
            /* empty */
          } else {
            setError(data.error_message || data.error);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [comment, onOpen, pixel, rules]);

  return (
    <Modal isOpen={open} onOpenChange={onOpen} placement="center">
      <ModalContent className="canvas-overlay-card">
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              Report Pixel
            </ModalHeader>
            <ModalBody>
              {error && (
                <Alert color="danger" title="Error" description={error} />
              )}
              {surrounding && (
                <div className="w-full h-52 bg-gray-200 dark:bg-gray-800 flex justify-center items-center">
                  <div className="w-[128px] h-[128px] bg-white">
                    <SmallCanvas
                      surrounding={surrounding}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              )}
              {pixel && (
                <Input
                  label="Pixel to report"
                  disabled
                  value={`#${pixel.id} (${pixel.x}, ${pixel.y}) ${pixel.userId}`}
                />
              )}
              <CheckboxGroup
                label="Rule(s)"
                isRequired
                onChange={(v) => setRules(v)}
              >
                {meta.data?.rules.map((rule) => (
                  <Checkbox value={rule.id} key={rule.id}>
                    {rule.name}
                  </Checkbox>
                ))}
                <Checkbox value="other">Other (describe below)</Checkbox>
              </CheckboxGroup>
              <Input
                label="Additional comments"
                isRequired={rules.indexOf("other") > -1}
                value={comment}
                onValueChange={(v) => setComment(v)}
              />
            </ModalBody>
            <ModalFooter>
              <Button onPress={onClose}>Close</Button>
              <Button
                color="primary"
                isLoading={loading}
                onPress={doSubmit}
                isDisabled={
                  rules.length === 0 ||
                  (rules.indexOf("other") > -1 && comment.length < 5)
                }
              >
                Submit
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
